import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import { Photo, PhotoSection } from "../../../types/venue";
import { VenueDocument } from "../types/venues.types";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs`;

interface ViewVenuePhotosProps {
  files: VenueDocument[];
  document_type: string;
}

type PdfState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "blob"; url: string }
  | { status: "google"; src: string }
  | { status: "error" };

// Returns true if the file URL points to a PDF
const isPdf = (src: string): boolean =>
  !!src?.toLowerCase().endsWith(".pdf") || src?.includes("application/pdf");

// ── Add at top of file ──

// ── PdfThumbnail component — add before ViewVenuePhotos ──
const PdfThumbnail: React.FC<{
  src: string;
  fileName: string;
  onClick: () => void;
}> = ({ src, fileName, onClick }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: src,
          withCredentials: false,
          cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/cmaps/`,
          cMapPacked: true,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const page = await pdf.getPage(1);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        // Scale to fit the 120px height thumbnail
        const desiredHeight = 120;
        const viewport = page.getViewport({ scale: 1 });
        const scale = desiredHeight / viewport.height;
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        await page.render({ canvas: canvas, viewport: scaledViewport }).promise;
      } catch (err) {
        console.warn("PDF thumbnail failed:", err);
        if (!cancelled) setFailed(true);
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        height: 120,
        borderRadius: 1,
        cursor: "pointer",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s ease",
        "&:hover": { transform: "scale(1.02)" },
        "&:hover .pdf-overlay": { opacity: 1 },
      }}
    >
      {failed ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <PictureAsPdfIcon sx={{ fontSize: 36, color: "#C29363" }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", px: 1 }}
          >
            {fileName}
          </Typography>
        </Box>
      ) : (
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      {/* Hover overlay */}
      <Box
        className="pdf-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.45)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          opacity: 0,
          transition: "opacity 0.2s",
        }}
      >
        <PictureAsPdfIcon sx={{ fontSize: 28, color: "#fff" }} />
        <Typography
          sx={{ fontSize: "0.65rem", color: "#fff", fontWeight: 600 }}
        >
          Click to open
        </Typography>
      </Box>

      {/* PDF badge */}
      <Box
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          bgcolor: "#C29363",
          borderRadius: 0.5,
          px: 0.5,
          py: 0.15,
        }}
      >
        <Typography
          sx={{ fontSize: "0.55rem", color: "#fff", fontWeight: 700 }}
        >
          PDF
        </Typography>
      </Box>
    </Box>
  );
};

const ViewVenuePhotos: React.FC<ViewVenuePhotosProps> = ({
  files,
  document_type,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [useAsCover, setUseAsCover] = useState(false);
  const [pdfState, setPdfState] = useState<PdfState>({ status: "idle" });

  const formatTitle = (type: string) =>
    type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const photoSections: PhotoSection[] = [
    {
      title: formatTitle(document_type),
      photos: files
        .filter((document) => document.file_type === document_type)
        .map((document) => ({
          id: document.id,
          src: document.file,
          alt: `${formatTitle(document_type)} ${document.id}`,
        })),
    },
  ];

  const getFileName = (src: string): string => {
    try {
      const raw = decodeURIComponent(src).split("/").pop() ?? "";
      const withoutExt = raw.replace(/\.[^.]+$/, "");
      const withoutSuffix = withoutExt.replace(/_[A-Za-z0-9]{6,12}$/, "");
      return withoutSuffix.replace(/_/g, " ").trim() || "PDF Document";
    } catch {
      return "PDF Document";
    }
  };

  const allPhotos: Photo[] = photoSections.flatMap((section) => section.photos);

  // Fetch PDF: try blob first, fall back to Google Docs viewer
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!selectedPhoto || !isPdf(selectedPhoto.src)) {
      setPdfState({ status: "idle" });
      return;
    }

    setPdfState({ status: "loading" });

    fetch(selectedPhoto.src)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfState({ status: "blob", url: objectUrl });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn(
          "Blob fetch failed, falling back to Google Docs viewer:",
          err,
        );
        setPdfState({
          status: "google",
          src: `https://docs.google.com/viewer?url=${encodeURIComponent(
            selectedPhoto.src,
          )}&embedded=true`,
        });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedPhoto]);

  const handlePhotoClick = (photo: Photo) => {
    const photoIndex = allPhotos.findIndex((p) => p.id === photo.id);
    setCurrentPhotoIndex(photoIndex);
    setSelectedPhoto(photo);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
    setUseAsCover(false);
    setPdfState({ status: "idle" });
  };

  const handlePrevPhoto = () => {
    const newIndex =
      currentPhotoIndex > 0 ? currentPhotoIndex - 1 : allPhotos.length - 1;
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(allPhotos[newIndex]);
  };

  const handleNextPhoto = () => {
    const newIndex =
      currentPhotoIndex < allPhotos.length - 1 ? currentPhotoIndex + 1 : 0;
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(allPhotos[newIndex]);
  };

  const handleUseAsCoverChange = async (checked: boolean) => {
    if (!selectedPhoto) {
      toast.error("Invalid venue or photo selection", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        style: {
          backgroundColor: colors.primary[900],
          color: "#333",
          border: "1px solid #C29363",
          zIndex: 9999,
        },
      });
      setUseAsCover(false);
      return;
    }

    setUseAsCover(checked);
    if (checked) {
      try {
        await apiService.post(`/api/venues/document/change-cover/`, {
          id: selectedPhoto.id,
        });
        toast.success("Cover photo updated successfully", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          style: {
            backgroundColor: colors.primary[900],
            color: colors.primary[700],
            border: "1px solid #C29363",
            zIndex: 9999,
          },
        });
      } catch (err) {
        let errorMessage = "Failed to update cover photo.";
        const errorData = (
          err as { response?: { data?: { detail?: string; message?: string } } }
        )?.response?.data;
        if (errorData?.detail) {
          errorMessage = errorData.detail;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
        console.error("Error updating cover photo:", err);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          style: {
            backgroundColor: colors.primary[900],
            color: colors.primary[700],
            border: "1px solid #C29363",
            zIndex: 9999,
          },
        });
        setUseAsCover(false);
      }
    }
  };

  const renderPdfViewer = () => {
    switch (pdfState.status) {
      case "loading":
        return (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              backgroundColor: colors.primary[800],
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 48, color: "#C29363" }} />
            <Typography variant="body2" color="text.secondary">
              Loading PDF...
            </Typography>
          </Box>
        );

      case "blob":
        return (
          <iframe
            src={pdfState.url}
            title="PDF Viewer"
            width="100%"
            height="100%"
            style={{ border: "none", flex: 1 }}
          />
        );

      case "google":
        return (
          <iframe
            src={pdfState.src}
            title="PDF Viewer"
            width="100%"
            height="100%"
            style={{ border: "none", flex: 1 }}
          />
        );

      default:
        return (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              backgroundColor: colors.primary[800],
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 48, color: "#C29363" }} />
            <Typography variant="body2" color="text.secondary">
              Unable to preview PDF.
            </Typography>
          </Box>
        );
    }
  };

  if (!allPhotos.length) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No {formatTitle(document_type).toLowerCase()} available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {photoSections.map((section) => (
          <Grid size={{ xs: 6, md: 6 }} key={section.title}>
            <Paper sx={{ p: 2, height: "100%", boxShadow: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {section.title}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {section.photos.map((photo) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={photo.id}>
                    {isPdf(photo.src) ? (
                      <PdfThumbnail
                        src={photo.src}
                        fileName={getFileName(photo.src)}
                        onClick={() => handlePhotoClick(photo)}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={photo.src}
                        alt={photo.alt}
                        sx={{
                          width: "100%",
                          height: 120,
                          objectFit: "cover",
                          borderRadius: 1,
                          cursor: "pointer",
                          transition: "transform 0.2s ease-in-out",
                          "&:hover": { transform: "scale(1.02)" },
                        }}
                        onClick={() => handlePhotoClick(photo)}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={!!selectedPhoto}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: colors.primary[900], borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {formatTitle(document_type)}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: "relative" }}>
          {selectedPhoto && (
            <>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {allPhotos.length > 1 && (
                  <IconButton
                    onClick={handlePrevPhoto}
                    sx={{
                      position: "absolute",
                      left: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: colors.primary[900],
                      boxShadow: 2,
                      zIndex: 1,
                      "&:hover": { backgroundColor: colors.primary[800] },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                )}

                {isPdf(selectedPhoto.src) ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: 480,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {renderPdfViewer()}
                    <Box
                      sx={{
                        p: 1,
                        textAlign: "center",
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        component="a"
                        href={selectedPhoto.src}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{
                          color: "#C29363",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        Open PDF in new tab ↗
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={selectedPhoto.src}
                    alt={selectedPhoto.alt}
                    sx={{ width: "100%", maxHeight: 400, objectFit: "contain" }}
                  />
                )}

                {allPhotos.length > 1 && (
                  <IconButton
                    onClick={handleNextPhoto}
                    sx={{
                      position: "absolute",
                      right: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: colors.primary[900],
                      boxShadow: 2,
                      zIndex: 1,
                      "&:hover": { backgroundColor: colors.primary[800] },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                )}
              </Box>

              {!isPdf(selectedPhoto.src) && (
                <Box sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useAsCover}
                        onChange={(e) =>
                          handleUseAsCoverChange(e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Use as cover"
                    sx={{ color: "text.secondary" }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ViewVenuePhotos;
